import { defineComponent } from "vue";
import * as yup from "yup";
import { mapState } from "vuex";

// Components
import MainBarMenuV2 from "@/components/layouts/MainBarMenuV2.vue";
import HeadingUser from "@/components/user/HeadingUser.vue";
import Typography from "@/components/general/Typography";
import Button from "@/components/general/Button";
import useInputs from "@/components/general/Inputs";
import Breadcrumb from "@/components/general/Breadcrumb";
import Steps from "@/components/legal/Steps";

// services
import processSevices from "@/services/legal/service.js";

const { Input, Select, Textarea, Date } = useInputs();
const string = yup
  .string()
  .nullable()
  .transform((curr, orig) => (orig === "" ? "" : curr));
const number = yup.number().nullable(true);
const object = yup
  .object()
  .shape({
    name: string,
    value: number | string,
  })
  .nullable();
const array = yup
  .array()
  .of(
    yup.object().shape({
      name: string,
      value: number | string,
    })
  )
  .nullable();

const stepThreeProcessFormSchema = yup.object().shape({
  demandados: array.required("Campo requerido"),
  tests: object.required("Campo requerido"),
  prescription: object.required("Campo requerido"),
  area: object.required("Campo requerido"),
  layer: object.required("Campo requerido"),
  status: object.required("Campo requerido"),
});

const stepTwoProcessFormSchema = yup.object().shape({
  radicado: string
    .required("Campo requerido")
    .min(7, "El radicado tiene que tener al menos 7 digitos")
    .max(23, "El radicado no puede superar los 23 digitos"),
  dateNotification: string,
  juzgado: object.required("Campo requerido"),
  causa: string.required("Campo requerido"),
  type: object.required("Campo requerido"),
  object: object.required("Campo requerido"),
  tutela: object.required("Campo requerido"),
  querella: object.required("Campo requerido"),
  provition: object.required("Campo requerido"),
  cuantia: number
    .typeError("age must be a number")
    .min(0, "Solo numeros positivos"),
});

const stepOneProcessFormSchema = yup.object().shape({
  demandante: string
    .required("Campo requerido")
    .matches(/[a-zA-Z ]{2,254}/, "Solo se permite texto"),
  cc: string,
  city: object.required("Campo requerido"),
  fuero: array,
  sindicato: array,
  age: number.min(0, "Solo numero positivos").integer("Solo numeros enteros"),
  worker: object,
});

export default defineComponent({
  components: {
    MainBarMenuV2,
    HeadingUser,
    Typography,
    Button,
    Input,
    Select,
    Textarea,
    Date,
    Breadcrumb,
    Steps,
  },
  data() {
    return {
      breadcrumb: [
        { name: "Procesos", active: false, link: "/legal/process" },
        { name: "Crear proceso", active: true, link: "" },
      ],
      step: 0,
      title: [
        "Datos del Demandante",
        "Datos del Litigio",
        "Datos del Demandado",
      ],
      listProgress: [
        { title: "Demandante", icon: "user" },
        { title: "Litigio", icon: "litigio" },
        { title: "Demandado", icon: "defendants" },
      ],
      isEdit: false,
      idCreate: 0,
      errors: {},
      formData: {
        cc: "",
        demandante: "",
        radicado: "",
        dateNotification: "",
        worker: null,
        demandados: null,
        type: null,
        juzgado: "",
        court_places: null,
        city: null,
        tests: null,
        cuantia: null,
        age: null,
        tutela: null,
        querella: null,
        sindicato: null,
        layer: null,
        area: null,
        risk: null,
        fuero: null,
        prescription: null,
        causa: null,
        provition: null,
        object: null,
        status: null,
      },
      saveCourts: [],
      options: {
        plaintiff_type: [],
        process_types: [],
        objects: [],
        complain: [],
        provision: [],
        defendants: [],
        prescription: [],
        areas: [],
        lawyers: [],
        courts: [],
        court_places: [],
        cities: [],
        charters: [],
        unions: [],
        evidence: [],
        guardianship_action: [],
        status: [],
      },
      isBusy: false,
    };
  },
  methods: {
    goBack() {
      this.$router.push("/legal/process");
    },
    confirmCancel() {
      this.$bvModal.show("modal-confirm");
    },
    closeModal(id) {
      this.$bvModal.hide(id);
    },
    validate(field) {
      if (this.step === 0) {
        stepOneProcessFormSchema
          .validateAt(field, this.formData)
          .then(() => {
            this.computedErrors = field;
          })
          .catch((err) => {
            this.computedErrors = err;
          });
      } else if (this.step === 1) {
        stepTwoProcessFormSchema
          .validateAt(field, this.formData)
          .then(() => {
            this.computedErrors = field;
          })
          .catch((err) => {
            this.computedErrors = err;
          });
      } else {
        stepThreeProcessFormSchema
          .validateAt(field, this.formData)
          .then(() => {
            this.computedErrors = field;
          })
          .catch((err) => {
            this.computedErrors = err;
          });
      }
    },
    submitData() {
      const me = this;
      stepThreeProcessFormSchema
        .validate(this.formData)
        .then(() => {
          const formData = {
            plaintiff_type: this.formData?.worker?.id,
            plaintiff_name: this.formData.demandante,
            plaintiff_document: this.formData.cc,
            plaintiff_age: this.formData.age,
            charters:
              this.formData.fuero && this.formData.fuero.length > 0
                ? this.formData.fuero.map((v) => {
                    return v.id;
                  })
                : null,
            unions:
              this.formData.sindicato && this.formData.sindicato.length > 0
                ? this.formData.sindicato.map((v) => {
                    return v.id;
                  })
                : null,
            city_id: this.formData.city.id,

            notification_date: this.formData.dateNotification,
            file_number: this.formData.radicado,

            process_type_id: this.formData.type.id,
            object_id: this.formData.object.id,
            causes: this.formData.causa,
            amount: this.formData.cuantia ? this.formData.cuantia : 0,
            court_id: this.formData.juzgado.id,
            guardianship_action: this.formData.tutela.id,
            complain: this.formData.querella.id,

            defendants: this.formData.demandados.map((v) => {
              return v.id;
            }),
            evidence: this.formData.tests.id,
            area_id: this.formData.area.id,
            prescription: this.formData.prescription.id,
            provision: this.formData.provition.id,

            status: this.formData.status.id,
            lawyer_id: this.formData.layer.id,
          };
          me.isBusy = true;
          processSevices
            .createProcess(formData)
            .then((response) => {
              if (!response.error) {
                this.$bvModal.show("modal-message");
                this.idCreate = response.data.id;
              } else {
                console.log(response);
                Object.values(response.error.detail).forEach((v) =>
                  this.makeToast(v)
                );
              }
            })
            .catch((err) => {
              const {
                response: { data },
              } = err;
              if (data?.error_code == 422) {
                const errors = Object.values(data?.message);
                errors.forEach((v) => this.makeToast(v));
              } else {
                this.makeToast('Error al crear el proceso, intente nuevamente');
              }
            })
            .finally(() => {
              me.isBusy = false;
            });
        })
        .catch((err) => {
          this.computedErrors = err;
        });
    },
    nextStep() {
      if (this.step === 0) {
        stepOneProcessFormSchema
          .validate(this.formData)
          .then(() => {
            this.step++;
          })
          .catch((err) => {
            this.computedErrors = err;
          });
      } else if (this.step === 1) {
        stepTwoProcessFormSchema
          .validate(this.formData)
          .then(() => {
            this.step++;
          })
          .catch((err) => {
            this.computedErrors = err;
          });
      }
    },
    prevStep() {
      this.step--;
    },
    initData() {
      processSevices.getOptions().then((response) => {
        this.options = response.data;
        this.saveCourts = response.data.courts;
        this.formData.layer = { id: this.user.id, name: this.user.name };
      });
    },
    filterCourt() {
      //Filtrar juzgados en minuscula y sin acentos
      if (this.formData.court_places?.id) {
        this.options.courts = this.saveCourts.filter(
          (v) =>
            v.city
              ?.toLowerCase()
              .normalize("NFD")
              .replace(/[\u0300-\u036f]/g, "") ===
            this.formData.court_places?.id
              ?.toLowerCase()
              .normalize("NFD")
              .replace(/[\u0300-\u036f]/g, "")
        );
      } else {
        this.options.courts = this.saveCourts;
      }
    },
    makeToast(msg) {
      this.$bvToast.toast(msg, {
        title: "Datos no guardados",
        autoHideDelay: 7000,
        appendToast: true,
        variant: "danger",
        solid: true,
        // noCloseButton: true,
      });
    },
    goEvaluate() {
      this.$router.push(`/legal/process/evaluate/${this.idCreate}`);
    },
  },
  mounted() {
    this.initData();
    this.makeToast();
  },
  computed: {
    computedErrors: {
      get() {
        return this.errors;
      },
      set(newErr) {
        if (typeof newErr === "string") {
          delete this.errors[newErr];
        } else {
          if (newErr.path !== undefined) {
            const auxError = { [newErr.path]: newErr.message };
            this.errors = auxError;
          } else {
            console.error(newErr);
          }
        }
      },
    },
    ...mapState("modCore", ["user"]),
  },
  watch: {
    court_places: {
      handler() {
        // this will be run immediately on component creation.
      },
    },
  },
});
